import { createContext, useContext, useEffect, useMemo } from "react";
import useThemeStore from "@/store/themeStore";
import useColorModeStore, {
  applyColorModeClass,
  applySurfaceTokens,
} from "@/store/colorModeStore";

const ThemeContext = createContext(null);

/**
 * Apply brand theme CSS variables to document root, then overlay color-mode surfaces.
 */
const applyThemeToDocument = (theme, resolvedMode) => {
  if (!theme) return;

  const root = document.documentElement;

  if (theme.colors && typeof theme.colors === "object") {
    try {
      Object.entries(theme.colors).forEach(([key, value]) => {
        if (value) {
          root.style.setProperty(`--${key}`, value);
        }
      });
    } catch (error) {
      console.error("[ThemeProvider] Error applying colors:", error);
    }
  }

  if (theme.gradients && typeof theme.gradients === "object") {
    if (theme.gradients.body) {
      root.style.setProperty("--gradient-body", theme.gradients.body);
    }
    if (theme.gradients.sidebar) {
      root.style.setProperty("--gradient-sidebar", theme.gradients.sidebar);
    }
  }

  if (theme.id) {
    root.setAttribute("data-theme", theme.id);
  }

  applyColorModeClass(resolvedMode);
  applySurfaceTokens(resolvedMode, theme);
};

export function ThemeProvider({ children }) {
  const activeTheme = useThemeStore((state) => state.activeTheme);
  const getActiveTheme = useThemeStore((state) => state.getActiveTheme);
  const fetchActiveTheme = useThemeStore((state) => state.fetchActiveTheme);
  const isLoading = useThemeStore((state) => state.isLoading);

  const themes = useThemeStore((state) => state.themes);
  const fetchAllThemes = useThemeStore((state) => state.fetchAllThemes);
  const activateTheme = useThemeStore((state) => state.activateTheme);
  const createTheme = useThemeStore((state) => state.createTheme);
  const updateTheme = useThemeStore((state) => state.updateTheme);
  const deleteTheme = useThemeStore((state) => state.deleteTheme);
  const applyPreviewTheme = useThemeStore((state) => state.applyPreviewTheme);
  const revertToActiveTheme = useThemeStore((state) => state.revertToActiveTheme);

  const colorMode = useColorModeStore((state) => state.mode);
  const resolvedColorMode = useColorModeStore((state) => state.resolved);
  const setColorMode = useColorModeStore((state) => state.setMode);
  const toggleColorMode = useColorModeStore((state) => state.toggle);
  const syncSystem = useColorModeStore((state) => state.syncSystem);

  useEffect(() => {
    fetchActiveTheme();
  }, [fetchActiveTheme]);

  // Follow OS preference when mode is "system"
  useEffect(() => {
    if (colorMode !== "system") return undefined;
    const mq = window.matchMedia("(prefers-color-scheme: dark)");
    const onChange = () => syncSystem();
    mq.addEventListener("change", onChange);
    return () => mq.removeEventListener("change", onChange);
  }, [colorMode, syncSystem]);

  useEffect(() => {
    if (activeTheme && (activeTheme.colors || activeTheme.id)) {
      applyThemeToDocument(activeTheme, resolvedColorMode);
    } else {
      console.warn("[ThemeProvider] Invalid activeTheme, using default");
      const defaultTheme = useThemeStore.getState().getActiveTheme();
      if (defaultTheme) {
        applyThemeToDocument(defaultTheme, resolvedColorMode);
      }
    }
  }, [activeTheme, resolvedColorMode]);

  const value = useMemo(
    () => ({
      theme: getActiveTheme(),
      isLoading,
      themes,
      fetchAllThemes,
      activateTheme,
      createTheme,
      updateTheme,
      deleteTheme,
      applyPreviewTheme,
      revertToActiveTheme,
      colorMode,
      resolvedColorMode,
      setColorMode,
      toggleColorMode,
      isDark: resolvedColorMode === "dark",
    }),
    [
      getActiveTheme,
      isLoading,
      themes,
      fetchAllThemes,
      activateTheme,
      createTheme,
      updateTheme,
      deleteTheme,
      applyPreviewTheme,
      revertToActiveTheme,
      colorMode,
      resolvedColorMode,
      setColorMode,
      toggleColorMode,
    ]
  );

  return <ThemeContext.Provider value={value}>{children}</ThemeContext.Provider>;
}

export function useTheme() {
  const ctx = useContext(ThemeContext);
  if (!ctx) throw new Error("useTheme must be used within ThemeProvider");
  return ctx;
}
